/**
 * src/api/locations.js — Address Book / Locations API
 *
 * Thin wrappers around the FastAPI /locations/ endpoints.
 * All calls are authenticated via the Bearer token in client.js.
 *
 * Endpoint reference (callitin-backend):
 *   GET    /locations/                — list + search + filter
 *   GET    /locations/{id}            — single record
 *   POST   /locations/                — create
 *   PUT    /locations/{id}            — full update
 *   PATCH  /locations/{id}/archive    — soft-delete (is_active → 0)
 *   PATCH  /locations/{id}/restore    — restore  (is_active → 1)
 */

import { request } from "./client";

/**
 * List saved locations with optional search, type, and status filters.
 *
 * @param {object} params
 * @param {string}  [params.q]       — full-text search: name, address, city, contact, phone
 * @param {string}  [params.type]    — location_type filter (vendor | hospital | …)
 * @param {boolean} [params.active]  — true = active only (default), false = archived only, omit = both
 * @param {number}  [params.limit]   — max rows (1–500, default 200)
 * @param {number}  [params.offset]  — pagination offset
 * @returns {Promise<Response>}      — body: { total, limit, offset, items: Location[] }
 */
export function listLocations({ q, type, active, limit, offset } = {}) {
  const qs = new URLSearchParams();
  if (q      !== undefined && q      !== "") qs.set("q",      q);
  if (type   !== undefined && type   !== "") qs.set("type",   type);
  if (active !== undefined)                  qs.set("active", String(active));
  if (limit  !== undefined)                  qs.set("limit",  String(limit));
  if (offset !== undefined)                  qs.set("offset", String(offset));
  const query = qs.toString() ? `?${qs}` : "";
  return request(`/locations/${query}`, {
    authContext: { message: "Your session expired while loading the Address Book." },
  });
}

/**
 * Fetch a single location by ID.
 * @param {string} id
 * @returns {Promise<Response>}
 */
export function getLocation(id) {
  return request(`/locations/${id}`, {
    authContext: { message: "Your session expired while loading a location." },
  });
}

/**
 * Create a new saved location.  Dispatcher / admin only.
 * @param {object} body — LocationIn payload
 * @returns {Promise<Response>}  — body: { id, created_at, message }
 */
export function createLocation(body) {
  return request("/locations/", {
    method: "POST",
    body,
    authContext: { message: "Your session expired while saving a location." },
  });
}

/**
 * Full update (PUT) of an existing location.  Dispatcher / admin only.
 * All writable fields are replaced with the supplied values.
 * @param {string} id
 * @param {object} body — LocationIn payload
 * @returns {Promise<Response>}  — body: { id, updated_at, message }
 */
export function updateLocation(id, body) {
  return request(`/locations/${id}`, {
    method: "PUT",
    body,
    authContext: { message: "Your session expired while updating a location." },
  });
}

/**
 * Soft-delete a location (is_active → 0).  Dispatcher / admin only.
 * The record is retained for historical reference.
 * @param {string} id
 * @returns {Promise<Response>}  — body: { id, is_active, updated_at, message }
 */
export function archiveLocation(id) {
  return request(`/locations/${id}/archive`, {
    method: "PATCH",
    authContext: { message: "Your session expired while archiving a location." },
  });
}

/**
 * Restore a previously archived location (is_active → 1).  Dispatcher / admin only.
 * @param {string} id
 * @returns {Promise<Response>}  — body: { id, is_active, updated_at, message }
 */
export function restoreLocation(id) {
  return request(`/locations/${id}/restore`, {
    method: "PATCH",
    authContext: { message: "Your session expired while restoring a location." },
  });
}
